import { getLocalTimeZone, today } from '@internationalized/date'

import { CalculadoraValoresTotaisSacocH } from '@/domain/simulador/calculadora-valores-totais-sacoch'
import { CalculadorPrazoSacocH } from '@/domain/simulador/factories/concrete/horizontal/sacoch/calculador-prazo-sacoch'
import { RotinaArquivoSacocH } from '@/domain/simulador/factories/concrete/horizontal/sacoch/rotina-arquivo-sacoch'
import type { TGruposParcelaBase } from '@/domain/simulador/factories/concrete/horizontal/sacocv'
import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import type {
  TSimulacaoCalculoPrazo,
  TSimulacaoCalculoValoresTotais,
  TSimulacaoFluxoPagamento,
  TSimulacaoCalculoRotinaArquivo,
} from '@/domain/simulador/factories/types/simulacao-calculo'
import type { TParametrosDinamicosSimulacaoSimulador, TSimulacaoSchema } from '@/domain/simulador/types/simulacao.type'
import { CalculadorFluxoDePagamentoBase } from '@/features/simulador/brasil/domain/calculador-fluxo-pagamento-base'
import { GerenciadorEntradasSacocH } from '@/features/simulador/brasil/domain/gerenciador-entradas-sacoch'
import { GerenciadorIntermediariasSacocH } from '@/features/simulador/brasil/domain/gerenciador-intermediarias-sacoch'
import { GerenciadorMensaisSacocH } from '@/features/simulador/brasil/domain/gerenciador-mensais-sacoch'
import type { TRotinaArquivo } from '@/features/simulador/brasil/http/create-simulacao.service'
import type { TGetSimulacaoDuplicarResponse } from '@/types/simulacao'
import { GruposParcelasApiMapper } from '@/utils/grupos-parcelas-api.mapper'

type TGruposParcelaSacocH = Pick<TGruposParcelaBase, 'entradas' | 'mensais' | 'intermediarias'>
type TSimulacaoSacocH = {
  condicaoDePrecoId: string
  tabelaDePrecoId: string
  bloco: { idExterno: string; nome: string }
  produto: { idExterno: string; nome: string; metragem?: string }
  dataSaldoCondicaoPreco: string
  dataPrevisaoDeEntregaDoBloco: string
  tipoDeAmortizacao: 'SacocH'
  prazoSemCorrecao?: number
  quantidadeMensais?: number
}
export class SimulacaoSacocH {
  protected readonly dataSimulacao: string = today(getLocalTimeZone()).toString()

  readonly entradas = new GerenciadorEntradasSacocH()
  readonly mensais = new GerenciadorMensaisSacocH()
  readonly intermediarias = new GerenciadorIntermediariasSacocH()

  protected criarSimulacaoBase(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSacocH {
    const { condicaoDePreco, tabelaDePreco, bloco, produto } = parametrosDinamicos

    return {
      condicaoDePrecoId: condicaoDePreco.id,
      tabelaDePrecoId: tabelaDePreco.id,
      bloco: { idExterno: String(bloco.idExterno), nome: bloco.nome },
      produto: {
        ...produto,
        idExterno: String(produto.idExterno),
      },
      dataSaldoCondicaoPreco: condicaoDePreco.dataSaldo,
      dataPrevisaoDeEntregaDoBloco: condicaoDePreco.previsaoDeEntregaDoBloco,
      tipoDeAmortizacao: 'SacocH',
      prazoSemCorrecao: condicaoDePreco.prazoSemCorrecao,
      quantidadeMensais: condicaoDePreco.quantidadeMensais,
    }
  }

  protected criarGruposParcela(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TGruposParcelaSacocH {
    const { condicaoDePreco, tabelaDePreco } = parametrosDinamicos
    const datasSinais = [
      condicaoDePreco.dataSinal1,
      condicaoDePreco.dataSinal2,
      condicaoDePreco.dataSinal3,
      condicaoDePreco.dataSinal4,
    ].filter((data): data is string => Boolean(data))

    const entradas = this.entradas.criarGrupoInicial(datasSinais, tabelaDePreco.valorBaseSinal, {
      data: condicaoDePreco.dataAto,
      valor: tabelaDePreco.valorBaseAto,
    })

    const mensais = this.mensais.criarGrupoInicial(
      condicaoDePreco.dataPrimeiraMensal,
      condicaoDePreco.dataUltimaMensal,
      tabelaDePreco.valorBaseMensais,
    )

    const intermediarias = this.intermediarias.criarGrupoInicial(
      condicaoDePreco.dataDasIntermediarias,
      tabelaDePreco.valorBaseIntermediarias,
    )

    return {
      entradas,
      intermediarias,
      mensais,
    }
  }

  criar = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSchema<'SacocH'> => {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    return {
      id: crypto.randomUUID(),
      ...simulacaoBase,
      ...grupos,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
      dataSimulacao: this.dataSimulacao,
    }
  }

  duplicar = (
    simulacao: TGetSimulacaoDuplicarResponse,
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoSchema<'SacocH'> => {
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    const { entradas, intermediarias, mensais } = new GruposParcelasApiMapper(simulacao).buildGruposParcelas()

    return {
      id: crypto.randomUUID(),
      simulacaoAnteriorId: simulacao.id,
      dataSimulacao: this.dataSimulacao,
      ...simulacaoBase,
      entradas,
      mensais,
      intermediarias,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  recalcular = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) => {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      ...simulacaoBase,
      ...grupos,
    }
  }

  calcularFluxoDePagamento(simulacao: TSimulacaoFluxoPagamento): CalculadorFluxoDePagamentoBase {
    return new CalculadorFluxoDePagamentoBase(simulacao.entradas, simulacao.mensais, simulacao.intermediarias)
  }

  calcularValoresTotais(simulacao: TSimulacaoCalculoValoresTotais) {
    return new CalculadoraValoresTotaisSacocH(simulacao)
  }

  calcularPrazo(simulacao: TSimulacaoCalculoPrazo) {
    return new CalculadorPrazoSacocH(simulacao)
  }

  calcularRotinaArquivo(
    grupos: TSimulacaoCalculoRotinaArquivo,
    parametrosRotinaArquivo: TParametrosRotinaArquivo,
  ): TRotinaArquivo | null {
    const rotinaArquivo = new RotinaArquivoSacocH(parametrosRotinaArquivo)
    return rotinaArquivo.calcular(grupos)
  }
}
